import React, { useState, useEffect } from 'react';
import { Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableDropdown from '../common/SearchableDropdown';
import DatePicker from '../common/DatePicker';
import { salesInvoiceService, customerService, productService, tenantService } from '../../services';
import { useToast } from '../../context/ToastContext';
import { roundAmountFields, toSafeFixed } from '../../utils/numberUtils';
import { inventoryService } from '../../services/api';

interface InvoiceItem {
  product_id: number;
  product_name?: string;
  hsn_code: string;
  batch_number: string;
  mrp: number;
  quantity: number;
  free_quantity: number;
  unit_price: number;
  discount_percent: number;
  gst_rate: number;
  cgst_rate: number;
  sgst_rate: number;
  cgst_amount: number;
  sgst_amount: number;
  taxable_amount: number;
  total_tax_amount: number;
  total_amount: number;
  line_discount_percent: number;
  line_discount_amount: number;
  description: string;
}

interface SalesInvoiceFormProps {
  onSave: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm?: boolean;
}

const SalesInvoiceForm: React.FC<SalesInvoiceFormProps> = ({ onSave, isCollapsed, onToggleCollapse, resetForm }) => {
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [agencyOptions, setAgencyOptions] = useState<{value: string, label: string}[]>([]);
  const [warehouses, setWarehouses] = useState<any[]>([]);
  const { showToast } = useToast();

  const generateInvoiceNumber = () => {
    const now = new Date();
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const tenantId = user.tenant_id || 1;
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const fff = String(now.getMilliseconds()).padStart(3, '0');
    return `SI-${tenantId}${dd}${mm}${yyyy}${hh}${min}${ss}${fff}`;
  };

  const [formData, setFormData] = useState({
    invoice_number: generateInvoiceNumber(),
    customer_id: '',
    agency_id: '',
    warehouse_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    status: 'POSTED',
    discount_percent: 0,
    roundoff: 0
  });

  const createEmptyItem = (): InvoiceItem => ({
    product_id: 0,
    product_name: '',
    hsn_code: '',
    batch_number: '',
    mrp: 0,
    quantity: 0,
    free_quantity: 0,
    unit_price: 0,
    discount_percent: 0,
    gst_rate: 0,
    cgst_rate: 0,
    sgst_rate: 0,
    cgst_amount: 0,
    sgst_amount: 0,
    taxable_amount: 0,
    total_tax_amount: 0,
    total_amount: 0,
    line_discount_percent: 0,
    line_discount_amount: 0,
    description: ''
  });

  const [items, setItems] = useState<InvoiceItem[]>([createEmptyItem()]);

  useEffect(() => {
    loadCustomers('');
    loadProducts();
    loadAgencies('').then(options => setAgencyOptions(options));
    loadWarehouses();
  }, []);

  useEffect(() => {
    if (resetForm) {
      setFormData({
        invoice_number: generateInvoiceNumber(),
        customer_id: '',
        agency_id: '',
        warehouse_id: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        status: 'POSTED',
        discount_percent: 0,
        roundoff: 0
      });
      setItems([createEmptyItem()]);
    }
  }, [resetForm]);

  const loadCustomers = async (search: string = '') => {
    try {
      const response = await customerService.getCustomers({ search, per_page: 50 });
      setCustomers(response.data);
      return response.data.map((c: any) => ({
        value: c.id.toString(),
        label: `${c.phone} | ${c.name}`
      }));
    } catch (error) {
      showToast('error', 'Failed to load customers');
      return [];
    }
  };

  const handleAddCustomer = async (inputValue: string) => {
    try {
      const parts = inputValue.split('|').map(p => p.trim());
      const phone = parts[0];
      const name = parts[1] || '';
      
      if (!phone || !name) {
        throw new Error('Phone and name are required');
      }
      
      const customerData = {
        phone,
        name,
        tenant_id: 1,
        is_active: true
      };
      
      const response = await customerService.createCustomer(customerData);
      const newCustomer = {
        value: response.data.id.toString(),
        label: `${phone} | ${name}`
      };
      
      await loadCustomers('');
      
      // Set the newly created customer as selected
      setFormData({ ...formData, customer_id: response.data.id.toString() });
      
      return newCustomer;
    } catch (error) {
      showToast('error', 'Failed to create customer');
      throw error;
    }
  };

  const handleAddAgency = async (inputValue: string) => {
    try {
      const parts = inputValue.split('|').map(p => p.trim());
      const phone = parts[0];
      const name = parts[1] || '';
      
      if (!phone || !name) {
        throw new Error('Phone and name are required');
      }
      
      const agencyData = {
        phone,
        name,
        tenant_id: 1,
        is_active: true
      };
      
      const response = await tenantService.createAgency(agencyData);
      const newAgency = {
        value: response.data.id.toString(),
        label: `${phone} | ${name}`
      };
      
      const updatedAgencies = await loadAgencies('');
      setAgencyOptions(updatedAgencies);
      
      // Set the newly created agency as selected
      setFormData({ ...formData, agency_id: response.data.id.toString() });
      
      return newAgency;
    } catch (error) {
      showToast('error', 'Failed to create agency');
      throw error;
    }
  };

  const loadProducts = async () => {
    try {
      const response = await productService.getProducts();
      setProducts(response.data);
    } catch (error) {
      showToast('error', 'Failed to load products');
    }
  };

  const loadAgencies = async (search: string) => {
    try {
      const response = await tenantService.getAgencies({ search, per_page: 50 });
      return response.data.map((agency: any) => ({
        value: agency.id.toString(),
        label: `${agency.phone} | ${agency.name}`
      }));
    } catch (error) {
      showToast('error', 'Failed to load agencies');
      return [];
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await inventoryService.getWarehouses({ per_page: 100 });
      setWarehouses(response.data.data || response.data || []);
    } catch (error) {
      showToast('error', 'Failed to load warehouses');
      setWarehouses([]);
    }
  };

  const handleProductChange = (index: number, value: string | number | (string | number)[]) => {
    const productId = Array.isArray(value) ? value[0] : value;
    const product = products.find(p => p.id === Number(productId));
    
    const newItems = [...items];
    newItems[index] = {
      ...newItems[index],
      product_id: Number(productId),
      product_name: product?.name ?? '',
      unit_price: product?.selling_price ?? product?.cost_price ?? 0,
      gst_rate: product?.gst_rate ?? 0
    };
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const handleItemChange = (index: number, field: keyof InvoiceItem, value: number | string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    setItems(newItems);
    calculateItemTotal(index, newItems);
  };

  const calculateItemTotal = (index: number, itemsArray: InvoiceItem[]) => {
    const item = itemsArray[index];
    const baseAmount = item.unit_price * item.quantity;
    const discountAmount = baseAmount * (item.discount_percent / 100);
    const taxableAmount = baseAmount - discountAmount;
    
    // Split GST into CGST and SGST (assuming intra-state)
    const cgstRate = item.gst_rate / 2;
    const sgstRate = item.gst_rate / 2;
    const cgstAmount = taxableAmount * (cgstRate / 100);
    const sgstAmount = taxableAmount * (sgstRate / 100);
    const totalTaxAmount = cgstAmount + sgstAmount;
    const total = taxableAmount + totalTaxAmount;

    itemsArray[index] = { 
      ...item, 
      cgst_rate: cgstRate,
      sgst_rate: sgstRate,
      cgst_amount: cgstAmount,
      sgst_amount: sgstAmount,
      taxable_amount: taxableAmount,
      total_tax_amount: totalTaxAmount,
      line_discount_amount: discountAmount,
      total_amount: total 
    };
    setItems([...itemsArray]);
  };

  const addItem = () => {
    setItems([...items, createEmptyItem()]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const calculateTotals = () => {
    const subtotal = items.reduce((sum, item) => sum + item.taxable_amount, 0);
    const taxAmount = items.reduce((sum, item) => sum + item.total_tax_amount, 0);
    const discountAmount = subtotal * (formData.discount_percent / 100);
    const finalTotal = subtotal + taxAmount - discountAmount;
    return { subtotal, discountAmount, finalTotal };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.customer_id) {
      showToast('error', 'Please select a customer');
      return;
    }

    const validItems = items.filter(item => item.product_id > 0 && item.quantity > 0);
    if (validItems.length === 0) {
      showToast('error', 'Please add at least one item');
      return;
    }

    try {
      const { finalTotal, subtotal, discountAmount } = calculateTotals();
      
      // Calculate total tax and CGST/SGST totals
      const cgstTotal = validItems.reduce((sum, item) => sum + item.cgst_amount, 0);
      const sgstTotal = validItems.reduce((sum, item) => sum + item.sgst_amount, 0);
      const taxTotal = validItems.reduce((sum, item) => sum + item.total_tax_amount, 0);
      
      const taxableTotal = validItems.reduce((sum, item) => sum + item.taxable_amount, 0);
      
      const invoiceData = {
        invoice_number: formData.invoice_number,
        customer_id: Number(formData.customer_id),
        warehouse_id: formData.warehouse_id ? Number(formData.warehouse_id) : 1, // Default to warehouse 1 if not selected
        invoice_date: formData.invoice_date,
        due_date: formData.due_date || undefined,
        status: formData.status,
        total_amount_base: finalTotal,
        subtotal_base: taxableTotal,
        discount_amount_base: discountAmount,
        tax_amount_base: taxTotal,
        cgst_amount_base: cgstTotal,
        sgst_amount_base: sgstTotal,
        igst_amount_base: 0,
        cess_amount_base: 0,
        items: validItems.map((item, index) => ({
          line_no: index + 1,
          product_id: item.product_id,
          product_name: item.product_name || '',
          quantity: item.quantity,
          free_quantity: item.free_quantity,
          uom: 'NOS',
          unit_price_base: item.unit_price,
          unit_cost_base: item.unit_price * 0.7, // Assuming 30% margin
          discount_percent: item.discount_percent,
          discount_amount_base: item.line_discount_amount,
          taxable_amount_base: item.taxable_amount,
          cgst_rate: item.cgst_rate,
          cgst_amount_base: item.cgst_amount,
          sgst_rate: item.sgst_rate,
          sgst_amount_base: item.sgst_amount,
          igst_rate: 0,
          igst_amount_base: 0,
          cess_rate: 0,
          cess_amount_base: 0,
          tax_amount_base: item.total_tax_amount,
          total_amount_base: item.total_amount,
          hsn_code: item.hsn_code || undefined,
          batch_number: item.batch_number || undefined,
          description: item.description || undefined
        }))
      };

      const roundedInvoiceData = roundAmountFields(invoiceData);
      await salesInvoiceService.createSalesInvoice(roundedInvoiceData);
      onSave();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to save invoice';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
      }
      
      showToast('error', errorMessage);
    }
  };

  const { subtotal, discountAmount, finalTotal } = calculateTotals();

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="border-b border-gray-200 flex justify-between items-center" style={{ padding: 'var(--erp-section-padding)' }}>
        <h2 className="font-medium text-gray-800" style={{ fontSize: 'var(--erp-datatable-title-font-size)', lineHeight: 'var(--erp-line-height)' }}>Create Sales Invoice</h2>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="text-gray-500 hover:text-gray-700"
        >
          {isCollapsed ? <ChevronDown className="erp-form-btn-icon" style={{ marginRight: 0 }} /> : <ChevronUp className="erp-form-btn-icon" style={{ marginRight: 0 }} />}
        </button>
      </div>

      {!isCollapsed && (
        <form onSubmit={handleSubmit} style={{ padding: 'var(--erp-card-padding)' }}>
          <div className="grid grid-cols-1 sm:grid-cols-6" style={{ gap: 'var(--erp-spacing-lg)', marginBottom: 'var(--erp-spacing-xl)' }}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Number</label>
              <input
                type="text"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100"
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Customer *</label>
              <SearchableDropdown
                options={customers.map(c => ({ value: c.id.toString(), label: `${c.phone} | ${c.name}` }))}
                value={formData.customer_id}
                onChange={(value) => setFormData({ ...formData, customer_id: (Array.isArray(value) ? value[0] : value).toString() })}
                onSearch={loadCustomers}
                allowAdd={true}
                onAdd={handleAddCustomer}
                placeholder="Type phone | name"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Agency</label>
              <SearchableDropdown
                options={agencyOptions}
                value={formData.agency_id}
                onChange={(value) => setFormData({ ...formData, agency_id: (Array.isArray(value) ? value[0] : value).toString() })}
                onSearch={loadAgencies}
                allowAdd={true}
                onAdd={handleAddAgency}
                placeholder="Type phone | name"
                multiple={false}
                searchable={true}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Invoice Date *</label>
              <DatePicker
                value={formData.invoice_date}
                onChange={(value) => setFormData({ ...formData, invoice_date: value })}
                placeholder="Select date"
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status *</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="DRAFT">DRAFT</option>
                <option value="POSTED">POSTED</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Due Date</label>
              <DatePicker
                value={formData.due_date}
                onChange={(value) => setFormData({ ...formData, due_date: value })}
                placeholder="Select due date"
                className="w-full"
              />
            </div>
          </div>

          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-800">Invoice Items</h3>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Item
            </button>
          </div>

          <div className="mb-6 overflow-x-auto">
            <table className="w-full border border-gray-200" style={{ minWidth: '1200px' }}>
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '180px' }}>Product</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '80px' }}>HSN</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '80px' }}>Batch</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '70px' }}>MRP</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '70px' }}>Price</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '60px' }}>Qty</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '60px' }}>Free</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '60px' }}>Disc%</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '70px' }}>GST%</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '80px' }}>Total</th>
                  <th className="px-2 py-2 text-center text-xs font-semibold text-gray-700 uppercase border-b" style={{ minWidth: '60px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                  {items.map((item, index) => (
                    <tr key={index} className="border-t">
                      <td className="px-2 py-2">
                        <SearchableDropdown
                          options={products.map(p => ({ value: p.id.toString(), label: p.name }))}
                          value={item.product_id.toString()}
                          onChange={(value) => handleProductChange(index, value)}
                          placeholder="Select product..."
                          multiple={false}
                          searchable={true}
                          className="w-full"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.hsn_code || ''}
                          onChange={(e) => handleItemChange(index, 'hsn_code', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="HSN"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="text"
                          value={item.batch_number || ''}
                          onChange={(e) => handleItemChange(index, 'batch_number', e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="Batch"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.mrp}
                          onChange={(e) => handleItemChange(index, 'mrp', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.free_quantity}
                          onChange={(e) => handleItemChange(index, 'free_quantity', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.discount_percent}
                          onChange={(e) => handleItemChange(index, 'discount_percent', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step="0.1"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-2">
                        <input
                          type="number"
                          value={item.gst_rate}
                          onChange={(e) => handleItemChange(index, 'gst_rate', parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded text-center"
                          step="0.1"
                          placeholder="0"
                        />
                      </td>
                      <td className="px-2 py-2 text-sm text-center font-medium">{toSafeFixed(item.total_amount, 2)}</td>
                      <td className="px-2 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="text-red-600 hover:text-red-800"
                          disabled={items.length === 1}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

          <div className="flex flex-col md:flex-row justify-end" style={{ marginTop: 'var(--erp-spacing-xl)', gap: 'var(--erp-spacing-lg)' }}>
            <div className="w-full md:w-1/2 erp-summary-container">
              <h4 className="font-semibold text-gray-800 text-sm" style={{ marginBottom: 'var(--erp-spacing-lg)' }}>Invoice Summary</h4>
              <div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Subtotal:</span>
                  <span className="erp-summary-value">{toSafeFixed(subtotal, 2)}</span>
                </div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Discount %:</span>
                  <span className="erp-summary-value">0.00%</span>
                </div>
                <div className="erp-summary-row">
                  <span className="erp-summary-label">Discount Amount:</span>
                  <span className="erp-summary-value">-{toSafeFixed(discountAmount, 2)}</span>
                </div>
                <div className="erp-summary-row" style={{ borderTop: '2px solid var(--erp-border-color)', paddingTop: 'var(--erp-spacing-md)' }}>
                  <span className="erp-summary-label font-semibold">Total:</span>
                  <span className="erp-summary-value font-bold text-blue-600">{toSafeFixed(finalTotal, 2)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2" style={{ marginTop: 'var(--erp-spacing-lg)' }}>
            <button
              type="submit"
              className="px-4 py-2 text-xs font-medium text-white bg-primary hover:bg-secondary rounded"
            >
              Create Invoice
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default SalesInvoiceForm;
